import { useEffect, useRef, useState } from 'react';
import configs from 'react-native-config';

import appsflyerSDK from '@webtrader/core/appsflyer';
import { IDeepLinkData } from '@webtrader/core/appsflyer/types';
import { DEMO_LOGINS } from '@webtrader/core/constants/account';
import { useAuth } from '@webtrader/core/services/auth';
import { useCompany, useCompanyAppsFlyerTracking } from '@webtrader/core/services/company';
import { validateCompanyAccess } from '@webtrader/core/validators/company';

import { handleAppsFlyerRoute } from '@/utils/appsflyer-routing';
import { checkIsAndroid } from '@/utils/device';
import { navigationRef } from '@/utils/navigation';

export const useAppsFlyerTrackingEnabled = () => {
  const appsFlyerEnabled = useCompanyAppsFlyerTracking();
  const isAppsFlyerAndroidEnabled = appsFlyerEnabled && configs.APPSFLYER_KEY && configs.APPSFLYER_KEY.length > 0;

  const isAppsFlyerIosEnabled =
    appsFlyerEnabled &&
    configs.APPSFLYER_KEY &&
    configs.APPSFLYER_KEY.length > 0 &&
    configs.APPSFLYER_APP_ID &&
    configs.APPSFLYER_APP_ID.length > 0;
  const appsFlyerTrackingEnabled = checkIsAndroid() ? isAppsFlyerAndroidEnabled : isAppsFlyerIosEnabled;

  return appsFlyerTrackingEnabled;
};

export const useAppsFlyerDeepLink = () => {
  const appsFlyerTrackingEnabled = useAppsFlyerTrackingEnabled();
  const { company, loading: companyLoading } = useCompany();
  const { splashLoading, authenticated, loginAs } = useAuth();
  const [deepLinkData, setDeepLinkData] = useState<IDeepLinkData | null>(null);
  const [isNavigationReady, setIsNavigationReady] = useState(navigationRef.isReady());

  const processedDataRef = useRef<IDeepLinkData | null>(null);

  useEffect(() => {
    if (!appsFlyerTrackingEnabled) {
      return undefined;
    }
    const unsubscribe = appsflyerSDK.subscribe((data) => {
      setDeepLinkData(data);
    });
    return () => {
      unsubscribe();
    };
  }, [appsFlyerTrackingEnabled]);

  // Ensure navigation is ready
  useEffect(() => {
    if (!appsFlyerTrackingEnabled || isNavigationReady) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (navigationRef.isReady()) {
        setIsNavigationReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [appsFlyerTrackingEnabled, isNavigationReady]);

  useEffect(() => {
    // 1. Guard: AppsFlyer not enabled for this tenant
    if (!appsFlyerTrackingEnabled || !deepLinkData) {
      return;
    }
    // 2. Guard: App is still loading (checking auth/company/navigation)
    if (splashLoading || companyLoading || !isNavigationReady) {
      return;
    }
    // 3. Guard: Deduplication via Reference Equality — prevents same notification from routing twice
    //    within the same JS lifecycle (e.g. useEffect re-runs due to dependency changes like auth state).
    if (deepLinkData === processedDataRef.current) {
      return;
    }
    const { hasDemoAccountCreation } = validateCompanyAccess(company);
    const isDemo = DEMO_LOGINS.includes(loginAs);
    handleAppsFlyerRoute(deepLinkData.deepLinkValue, authenticated, hasDemoAccountCreation, isDemo, company);
    processedDataRef.current = deepLinkData;
  }, [
    appsFlyerTrackingEnabled,
    deepLinkData,
    splashLoading,
    companyLoading,
    isNavigationReady,
    authenticated,
    company,
    loginAs
  ]);
};
